import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import successToast from "../../../components/toast/SuccessToast";

export default class PaymentReminderFeature {

    constructor(application) {
        this._application = application;
        this._isOpen = false;
        this._taxpayerIdentificationNumber = null;
        this._subject = '';
        this._receivers = [];
        this._blindCopyReceivers = [];
        this._overdueInvoices = [];
        this._selectedInvoiceNumbers = new Set();
        this._paymentTrackRecord = null;
        this._customerDebtSummary = null;
        this._message = '';
        this._errorMessage = '';
        this._isSending = false;
        this._hasPreview = false;

        makeObservable(this, {
            _isOpen: observable,
            _subject: observable,
            _receivers: observable,
            _blindCopyReceivers: observable,
            _overdueInvoices: observable,
            _selectedInvoiceNumbers: observable,
            _paymentTrackRecord: observable,
            paymentTrackRecord: computed,
            _customerDebtSummary: observable,
            customerDebtSummary: computed,
            amountToClaim: computed,
            hasSelectedInvoices: computed,
            _message: observable,
            _errorMessage: observable,
            _isSending: observable,
            _hasPreview: observable,
            hasPreview: computed,
            isOpen: computed,
            subject: computed,
            receivers: computed,
            blindCopyReceivers: computed,
            overdueInvoices: computed,
            message: computed,
            errorMessage: computed,
            isSending: computed,
            canSend: computed,
            open: action,
            close: action,
            setMessage: action,
            selectInvoice: action,
            deselectInvoice: action,
        })
    }

    get isOpen() {
        return this._isOpen;
    }

    get subject() {
        return this._subject;
    }

    get receivers() {
        return this._receivers;
    }

    get blindCopyReceivers() {
        return this._blindCopyReceivers;
    }

    get overdueInvoices() {
        return this._overdueInvoices;
    }

    get customerDebtSummary() {
        return this._customerDebtSummary;
    }

    get hasSelectedInvoices() {
        return this._selectedInvoiceNumbers.size > 0;
    }

    get amountToClaim() {
        const selectedInvoices = this._overdueInvoices.filter(overdueInvoice => this.isSelected(overdueInvoice.number));
        return selectedInvoices.reduce((total, overdueInvoice) => total + overdueInvoice.pendingBalance.amount, 0);
    }

    get paymentTrackRecord() {
        return this._paymentTrackRecord;
    }

    get message() {
        return this._message;
    }

    get errorMessage() {
        return this._errorMessage;
    }

    get isSending() {
        return this._isSending;
    }

    get hasPreview() {
        return this._hasPreview;
    }

    get canSend() {
        return this._message.trim().length > 0 && this.hasSelectedInvoices && !this._isSending;
    }

    setMessage(message) {
        this._message = message;
    }

    isSelected(invoiceNumber) {
        return this._selectedInvoiceNumbers.has(invoiceNumber);
    }

    selectInvoice(invoiceNumber) {
        this._selectedInvoiceNumbers.add(invoiceNumber);
    }

    deselectInvoice(invoiceNumber) {
        this._selectedInvoiceNumbers.delete(invoiceNumber);
    }

    async open(taxpayerIdentificationNumber) {
        this._taxpayerIdentificationNumber = taxpayerIdentificationNumber;
        this._errorMessage = '';
        this._hasPreview = false;
        this._subject = '';
        this._receivers = [];
        this._blindCopyReceivers = [];
        this._overdueInvoices = [];
        this._selectedInvoiceNumbers = new Set();
        this._paymentTrackRecord = null;
        this._customerDebtSummary = null;
        this._message = '';
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._subject = response.subject();
                this._receivers = response.receivers();
                this._blindCopyReceivers = response.blindCopyReceivers();
                this._overdueInvoices = response.overdueInvoices();
                this._selectedInvoiceNumbers = new Set(this._overdueInvoices.map(overdueInvoice => overdueInvoice.number));
                this._paymentTrackRecord = response.paymentTrackRecord();
                this._customerDebtSummary = response.customerDebtSummary();
                this._message = response.message();
                this._hasPreview = true;
                this._isOpen = true;
            },
            handlesError: (response) => {
                this._errorMessage = this._errorMessageFor(response, 'No se pudo obtener la vista previa del recordatorio de pago');
                this._isOpen = true;
            }
        });
        return this._application.apiClient().getPaymentReminderPreview(taxpayerIdentificationNumber, responseHandler);
    }

    _errorMessageFor(response, unexpectedErrorMessage) {
        const errorMessagesByCode = {
            'customer_without_collections_email_error': 'El cliente no tiene mail de cobranzas cargado. No se puede enviar el recordatorio.',
            'customer_without_overdue_invoices_error': 'El cliente no tiene facturas vencidas. No hay nada que recordarle.',
            'blank_payment_reminder_message_error': 'El mensaje del recordatorio no puede estar vacío.',
            'invoice_is_not_an_overdue_invoice_of_customer_error': 'Alguna de las facturas seleccionadas ya no está vencida con saldo pendiente. Volvé a abrir el recordatorio.',
        };
        const knownError = response.errors().find(error => error.code in errorMessagesByCode);
        return knownError ? errorMessagesByCode[knownError.code] : unexpectedErrorMessage;
    }

    close() {
        this._isOpen = false;
    }

    async send() {
        this._isSending = true;
        this._errorMessage = '';
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: () => {
                this._isSending = false;
                this._isOpen = false;
                successToast('Recordatorio enviado correctamente');
            },
            handlesError: (response) => {
                this._isSending = false;
                this._errorMessage = this._errorMessageFor(response, 'No se pudo enviar el recordatorio de pago');
            }
        });
        return this._application.apiClient().sendPaymentReminder(
            this._taxpayerIdentificationNumber, this._message, [...this._selectedInvoiceNumbers], responseHandler);
    }

}
